import type { ReactNode } from 'react';
import { PARTIES, PARTY_ORDER, type GlobalAggregate, type PartyId } from '../lib/types';

interface Props {
  aggregate: GlobalAggregate;
}

const nf = new Intl.NumberFormat('sv-SE');

/**
 * Närmaste strid — minsta gap mellan topp två i `mostContested`.
 */
function tightestRace(aggregate: GlobalAggregate) {
  let tightest: { name: string; leader: PartyId; runnerUp: PartyId; gap: number } | null = null;
  for (const item of aggregate.mostContested) {
    const sorted = PARTY_ORDER
      .map(p => ({ p, c: item.followerCounts[p] ?? 0 }))
      .filter(x => x.c > 0)
      .sort((a, b) => b.c - a.c);
    if (sorted.length < 2) continue;
    const gap = sorted[0].c - sorted[1].c;
    if (!tightest || gap < tightest.gap) {
      tightest = {
        name: item.districtName,
        leader: sorted[0].p,
        runnerUp: sorted[1].p,
        gap,
      };
    }
  }
  return tightest;
}

/**
 * Bredast spridning — hur många distrikt det ledande partiet håller.
 * Enkelt men ger en bild av "vem dominerar kartan just nu".
 */
function widestReach(aggregate: GlobalAggregate) {
  const byParty = aggregate.totals.districtsByParty;
  const sorted = PARTY_ORDER
    .map(p => ({ p, c: byParty[p] ?? 0 }))
    .sort((a, b) => b.c - a.c);
  const top = sorted[0];
  if (!top || top.c === 0) return null;
  return { party: top.p, count: top.c };
}

/**
 * Underdog — partiet med minst distrikt (men minst 1).
 */
function smallestFoothold(aggregate: GlobalAggregate) {
  const byParty = aggregate.totals.districtsByParty;
  const withAny = PARTY_ORDER
    .map(p => ({ p, c: byParty[p] ?? 0 }))
    .filter(x => x.c > 0)
    .sort((a, b) => a.c - b.c);
  if (!withAny.length) return null;
  return { party: withAny[0].p, count: withAny[0].c };
}

export function FunFacts({ aggregate }: Props) {
  const tight = tightestRace(aggregate);
  const reach = widestReach(aggregate);
  const underdog = smallestFoothold(aggregate);

  const lansByParty = aggregate.totals.lansByParty ?? {};
  const fullyOwnedByParty = aggregate.totals.fullyOwnedLansByParty ?? {};

  const byLanCount = PARTY_ORDER
    .map(p => ({ p, lans: (lansByParty[p] ?? []).length }))
    .sort((a, b) => b.lans - a.lans);
  const widestLan = byLanCount[0] && byLanCount[0].lans > 0 ? byLanCount[0] : null;

  const fullControl = PARTY_ORDER
    .map(p => ({ p, count: (fullyOwnedByParty[p] ?? []).length }))
    .filter(x => x.count > 0)
    .sort((a, b) => b.count - a.count)[0] ?? null;

  const facts: { title: string; body: ReactNode }[] = [];

  if (reach) {
    facts.push({
      title: 'Störst på kartan',
      body: (
        <>
          <strong>{PARTIES[reach.party].name}</strong> håller{' '}
          <strong>{nf.format(reach.count)}</strong> distrikt, flest av alla partier just nu.
        </>
      ),
    });
  }

  if (tight) {
    facts.push({
      title: 'Jämnaste striden',
      body: (
        <>
          I <strong>{tight.name}</strong> skiljer det bara{' '}
          <strong>{nf.format(tight.gap)}</strong> anhängare mellan{' '}
          {PARTIES[tight.leader].name} och {PARTIES[tight.runnerUp].name}.
        </>
      ),
    });
  }

  if (widestLan) {
    facts.push({
      title: 'Breddast på kartan',
      body: (
        <>
          <strong>{PARTIES[widestLan.p].name}</strong> har fotfäste i{' '}
          <strong>{widestLan.lans}</strong> av 21 landskap.
        </>
      ),
    });
  }

  if (fullControl) {
    facts.push({
      title: 'Fullständig kontroll',
      body: (
        <>
          <strong>{PARTIES[fullControl.p].name}</strong> dominerar{' '}
          <strong>{fullControl.count}</strong>{' '}
          {fullControl.count === 1 ? 'landskap' : 'landskap'} helt.
        </>
      ),
    });
  }

  if (underdog && reach && underdog.party !== reach.party) {
    facts.push({
      title: 'Liten men seg',
      body: (
        <>
          <strong>{PARTIES[underdog.party].name}</strong> håller bara{' '}
          <strong>{nf.format(underdog.count)}</strong>{' '}
          {underdog.count === 1 ? 'distrikt' : 'distrikt'}, men kampen är igång.
        </>
      ),
    });
  }

  if (!facts.length) {
    return (
      <section className="fun-facts" id="visste-du-att">
        <h2>Visste du att…</h2>
        <p className="empty-state">Spelet har precis startat. Kom tillbaka när striderna tar fart.</p>
      </section>
    );
  }

  return (
    <section className="fun-facts" id="visste-du-att">
      <h2>Visste du att…</h2>
      <div className="fact-grid">
        {facts.map(f => (
          <div className="fact-card" key={f.title}>
            <h3>{f.title}</h3>
            <p>{f.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
